import { useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useItem, useList } from '../api/queries'
import type { Evidence, InspectionDetail, InspectionList, ScoreExplanation } from '../api/types'
import { useUser } from '../auth/AuthContext'
import { Card, ErrorState, LoadingState, PageHeader, Pagination, PillFilter, RouteCriticalBadge, SearchBox, StalenessBanner, StatusBadge } from '../components/ui'
import { BreakdownView } from '../features/inspections/BreakdownView'
import { dateFlagNote } from '../features/inspections/breakdown'
import { inspectionNotes, inspectionText } from '../features/inspections/presentation'
import { InspectionsTable } from '../features/tables/tables'
import { BAND_ORDER, bandMeta } from '../lib/bands'
import { formatDate, humanize, levelLabel } from '../lib/format'
import { useDebounced } from '../lib/hooks'
import { can } from '../lib/permissions'

export function InspectionsPage() {
  const user = useUser()
  const [params, setParams] = useSearchParams()
  const band = params.get('band') ?? ''
  const current = params.get('current') === '1' || params.get('is_current') === 'true'
  const [search, setSearch] = useState('')
  const [pageNo, setPageNo] = useState(1)
  const ordering = '-inspected_on'
  const q = useDebounced(search)
  const set = (k: string, v: string) => { const n = new URLSearchParams(params); if (v) n.set(k, v); else n.delete(k); setParams(n, { replace: true }); setPageNo(1) }
  const list = useList<InspectionList>('/api/inspections/', { band, is_current: current ? 'true' : undefined, search: q, page: pageNo, ordering, page_size: 50 })
  return (
    <div className="page">
      <PageHeader title="Inspecciones" subtitle="Historial de solo adición: una corrección es una nueva inspección" actions={can(user.role, 'inspection.create') ? <Link className="btn btn--primary" to="/inspections/new">Nueva inspección</Link> : undefined} />
      <Card>
        <div className="toolbar">
          <SearchBox label="Buscar inspecciones" value={search} onChange={setSearch} placeholder="Buscar equipo" />
          <label className="check"><input type="checkbox" checked={current} onChange={(e) => set('current', e.target.checked ? '1' : '')} /> Solo vigentes</label>
        </div>
        <div className="pills" role="group" aria-label="Banda">
          <PillFilter active={band === ''} onClick={() => set('band', '')}>Todos</PillFilter>
          {BAND_ORDER.map((b) => <PillFilter key={b} active={band === b} onClick={() => set('band', b)}>{bandMeta(b).label}</PillFilter>)}
        </div>
        <InspectionsTable rows={list.data?.results} loading={list.isPending} error={list.error} onRetry={() => void list.refetch()} />
        {list.data ? <Pagination page={pageNo} pageSize={50} count={list.data.count} onPage={setPageNo} /> : null}
      </Card>
    </div>
  )
}

const SHEET_ROWS: [keyof InspectionDetail, string][] = [
  ['vibration_mm_s', 'Vibración (mm/s)'], ['measured_current_a', 'Corriente medida (A)'], ['measured_voltage_v', 'Voltaje medido (V)'],
  ['reducer_temperature_c', 'Temperatura del reductor (°C)'],
  ['source_reported_motor_total', 'Total de motor informado (referencia)'],
  ['measured_frequency_hz', 'Frecuencia medida (Hz)'], ['motor_temperature_c', 'Temperatura del motor (°C)'], ['accessories', 'Accesorios'],
]

function SheetReadingsCard({ i }: { i: InspectionDetail }) {
  const rows = SHEET_ROWS.filter(([k]) => i[k] !== null && i[k] !== undefined && i[k] !== '')
  if (!i.vibration_level && rows.length === 0) return null
  return (
    <Card title="Mediciones y accesorios">
      <dl className="defs">
        {i.vibration_level ? <><dt>Vibraciones</dt><dd>{levelLabel(i.vibration_level)}</dd></> : null}
        {rows.map(([k, label]) => <div key={k}><dt>{label}</dt><dd>{String(i[k])}</dd></div>)}
      </dl>
      <p className="muted">Datos informativos: no puntúan ni cambian la banda.</p>
    </Card>
  )
}

export function InspectionDetailPage() {
  const { id } = useParams()
  const insp = useItem<InspectionDetail>(`/api/inspections/${id}/`)
  const score = useItem<ScoreExplanation>(`/api/inspections/${id}/score/`)
  const evidence = useItem<Evidence[]>(`/api/inspections/${id}/evidence/`)
  if (insp.isPending || score.isPending) return <LoadingState label="Cargando la inspección…" />
  if (insp.isError) return <ErrorState error={insp.error} onRetry={() => void insp.refetch()} />
  if (score.isError) return <ErrorState error={score.error} onRetry={() => void score.refetch()} />
  const i = insp.data
  const flag = dateFlagNote(i.date_flag)
  return (
    <div className="page">
      <PageHeader title={`Inspección del ${formatDate(i.inspected_on)}`} subtitle={<><Link to={`/assets/${i.equipment}`}>{i.equipment_name}</Link> · <span className="mono">{i.equipment_code}</span> {i.is_route_critical ? <RouteCriticalBadge /> : null}</>}
        actions={<>{i.is_current ? <StatusBadge tone="info" label="Vigente" /> : null}<StatusBadge tone="neutral" label={humanize(i.status)} /></>} />
      <StalenessBanner inspectedOn={i.inspected_on} />
      {flag ? <p role="note" className="callout callout--warn"><strong>{humanize(i.date_flag)}.</strong> {flag}</p> : null}
      <div className="grid grid--2-1">
        <Card title="Puntaje explicado"><BreakdownView score={score.data} /></Card>
        <div className="stack">
          <Card title="Contexto">
            <dl className="defs"><dt>Estado de operación</dt><dd>{inspectionText(humanize(i.operating_state))}</dd><dt>Plantilla</dt><dd>{score.data.template.code} v{score.data.template.version}</dd><dt>Origen</dt><dd>{humanize(i.source)}</dd>{i.source_file ? <><dt>Archivo</dt><dd className="mono">{i.source_file}{i.source_sheet ? ` / ${i.source_sheet}` : ''}</dd></> : null}<dt>Inspector</dt><dd>{i.inspector_name || '—'}</dd></dl>
            {i.technician_name ? <p><strong>Técnico revisor: </strong>{i.technician_name}</p> : null}
            {i.diagnosis ? <div><h3>Diagnóstico</h3><p style={{ whiteSpace: 'pre-wrap' }}>{i.diagnosis}</p></div> : null}
            {i.recommendations ? <div><h3>Recomendaciones</h3><p style={{ whiteSpace: 'pre-wrap' }}>{i.recommendations}</p></div> : null}
            {i.notes ? <p>{inspectionNotes(i.notes)}</p> : null}
          </Card>
          <SheetReadingsCard i={i} />
          <Card title="Fotos">{evidence.data?.length ? <div className="photos">{evidence.data.map((e) => <a key={e.id} href={e.url}><img src={e.url} alt={e.original_name} loading="lazy" /></a>)}</div> : <p className="muted">Sin fotos.</p>}</Card>
        </div>
      </div>
    </div>
  )
}
